// Making one person's numbers comparable to another's.
//
// One member uses the whole scale, another rates everything between seven and ten.
// Comparing those raw is invalid, so every cross-member comparison runs on a
// percentile rank inside that member's own rated set. Raw scores stay visible in
// the interface, because people want to see the number they chose; only the
// internal comparison uses the converted value.
//
// Two corrections sit on top of that. A member who rated thirty artists is not
// interchangeable with one who rated a hundred and eighty, so an unrated artist is
// missing data and never an implicit zero. And a seeded value contributes at a
// fraction of a manual one, because a system-guessed nine outvoting a human-chosen
// eight destroys trust the first time anyone notices.

import type { TuningConfig } from './config.ts';
import { DEFAULT_CONFIG } from './config.ts';
import type { Id, Rating } from './types.ts';

export interface NormalizedRating {
  artistId: Id;
  /** Percentile rank inside this member's own rated set, as a fraction. */
  value: number;
  /** How much this number counts against a manual rating from the same member. */
  weight: number;
  source: Rating['source'];
  rawScore: number;
}

/** Converts one member's ratings to percentile ranks with confidence weights. */
export function normalizeMember(
  ratings: Rating[],
  cfg: TuningConfig = DEFAULT_CONFIG,
): Map<Id, NormalizedRating> {
  const manual = ratings.filter((r) => r.source !== 'seeded');
  // The percentile frame is the member's manual ratings when there are enough of
  // them, because seeded values cluster and would flatten the distribution.
  const frame = manual.length >= 5 ? manual : ratings;
  const scores = frame.map((r) => r.score).sort((a, b) => a - b);

  const rank = (score: number): number => {
    if (scores.length === 0) return 0.5;
    let below = 0;
    let equal = 0;
    for (const s of scores) {
      if (s < score) below++;
      else if (s === score) equal++;
    }
    // Midrank, so a member who gave five artists the same score does not have them
    // all sitting at the top or all at the bottom of their own distribution.
    return (below + equal / 2) / scores.length;
  };

  const out = new Map<Id, NormalizedRating>();
  for (const r of ratings) {
    out.set(r.artistId, {
      artistId: r.artistId,
      value: rank(r.score),
      weight: r.source === 'seeded' ? cfg.seededConfidenceDiscount : 1,
      source: r.source,
      rawScore: r.score,
    });
  }
  return out;
}

export interface MemberView {
  userId: Id;
  displayName: string;
  normalized: Map<Id, NormalizedRating>;
  /** How many artists this member rated by hand, used for coverage correction. */
  manualCount: number;
}

/** Builds the comparable view of every member from their raw ratings. */
export function buildMemberViews(
  ratingsByUser: Map<Id, { displayName: string; ratings: Rating[] }>,
  cfg: TuningConfig = DEFAULT_CONFIG,
): MemberView[] {
  const out: MemberView[] = [];
  for (const [userId, entry] of ratingsByUser) {
    out.push({
      userId,
      displayName: entry.displayName,
      normalized: normalizeMember(entry.ratings, cfg),
      manualCount: entry.ratings.filter((r) => r.source !== 'seeded').length,
    });
  }
  return out;
}

/** Returns a member's comparable value for an artist, or null when there is none. */
export function valueFor(view: MemberView, artistId: Id): NormalizedRating | null {
  return view.normalized.get(artistId) ?? null;
}

/** Buckets a comparable value into the band the crew screen shows by default. */
export function bandOf(value: number): 'high' | 'medium' | 'low' {
  if (value >= 0.7) return 'high';
  if (value >= 0.4) return 'medium';
  return 'low';
}
